import { QuoteLineItemProps } from '@partsbadger/types';

/** While the socket is down updates only arrive by asking, so ask often. */
export const DISCONNECTED_REFRESH_MS = 10_000;
/** While it is connected the socket brings the updates: asking is only a safety net for a lost event. */
export const CONNECTED_REFRESH_MS = 45_000;

type AnalysisStatus = { completed?: boolean } | null | undefined;

const analysisPending = (status: AnalysisStatus) => status != null && status.completed === false;

/**
 * Whether something of the form is still being worked on: a 3D or 2D analysis that has not
 * ended, the Drawing File Analyzer answering with suggestions, or a price being calculated. A failed analysis has ended, so it is not waited for.
 */
export const hasPendingWork = (lineItems: ReadonlyArray<QuoteLineItemProps>): boolean =>
    lineItems.some(
        item =>
            analysisPending(item.product?.analysis_status?.file_3d) ||
            analysisPending(item.product?.analysis_status?.file_2d) ||
            Boolean(item.product?.analysis_status?.file_2d?.suggestions_pending) ||
            Boolean(item.is_pricing_in_progress)
    );

/** How long to wait before asking again, or null when there is nothing to wait for. */
export const getRefreshIntervalMs = ({
    pending,
    socketConnected,
}: {
    pending: boolean;
    socketConnected: boolean;
}): number | null => {
    if (!pending) return null;
    return socketConnected ? CONNECTED_REFRESH_MS : DISCONNECTED_REFRESH_MS;
};

const isTabHidden = () => typeof document !== 'undefined' && document.hidden;

/**
 * Asks for a refresh every `getIntervalMs()` while it answers a number, one at a time, and ends
 * by itself when it answers null. A failing refresh does not stop it, and a tab nobody is
 * looking at is skipped (it keeps its schedule). Returns the function that stops it.
 */
export const startPendingRefresh = (
    getIntervalMs: () => number | null,
    refresh: () => Promise<unknown>,
    isHidden: () => boolean = isTabHidden
): (() => void) => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
        if (stopped) return;
        const interval = getIntervalMs();
        if (interval == null) return;
        timer = setTimeout(async () => {
            if (!isHidden()) {
                try {
                    await refresh();
                } catch (error) {
                    console.error('Refreshing the quote failed:', error);
                }
            }
            schedule();
        }, interval);
    };

    schedule();

    return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
    };
};
